/**
 * 条件判定ユーティリティ
 *
 * 条件確認画面で使用する判定ロジックとフォーマット関数を提供
 */
import { TitleCondition, LeftRightValue } from 'api/condition'

// ============================================
// 維持タイトル計算
// ============================================

/**
 * 維持タイトルを計算する
 * 最高タイトルから降順に判定し、維持条件を満たすタイトルIDを返す
 *
 * @param maxTitleId 暫定タイトルID（この値以下で維持可能なタイトルを探す）
 * @param maintenanceAmounts 左右の維持実績
 * @param titleConditions 全タイトルの条件データ
 * @returns 維持可能なタイトルID
 */
export const calculateMaintenanceTitle = (
  maxTitleId: number,
  maintenanceAmounts: LeftRightValue,
  titleConditions: TitleCondition[]
): number => {
  // 最高タイトルから降順に判定
  for (let titleId = maxTitleId; titleId >= 1; titleId--) {
    const condition = titleConditions.find((c) => c.titleId === titleId)

    // 維持条件がないタイトルは維持とみなす
    if (!condition?.maintenance?.amount) {
      return titleId
    }

    // 維持条件の達成判定（amountは左右各の値）
    const required = condition.maintenance.amount
    if (maintenanceAmounts.left >= required && maintenanceAmounts.right >= required) {
      return titleId
    }
  }

  // 最低タイトル（SPC）を返す
  return 1
}

// ============================================
// フォーマット関数
// ============================================

/**
 * フォーマット済み金額文字列から数値を取得（formatAmountの逆変換）
 * @example
 * parseFormattedAmount("1億2,345万6,789円") // 123456789
 * parseFormattedAmount("1億123円")          // 100000123
 * parseFormattedAmount("1,234万5,678円")    // 12345678
 * parseFormattedAmount("3,456円")           // 3456
 */
export const parseFormattedAmount = (formatted: string): number => {
  let amount = 0

  // 億の部分を抽出
  const okuMatch = formatted.match(/([0-9,]+)億/)
  if (okuMatch) {
    amount += parseInt(okuMatch[1].replace(/,/g, ''), 10) * 100000000
  }

  // 万の部分を抽出
  const manMatch = formatted.match(/([0-9,]+)万/)
  if (manMatch) {
    amount += parseInt(manMatch[1].replace(/,/g, ''), 10) * 10000
  }

  // 端数（円の前の数値）を抽出
  // パターン: 万の後、億の後（万がない場合）、または単独
  if (manMatch) {
    // 万の後の端数: "万123円"
    const afterManMatch = formatted.match(/万([0-9,]+)円/)
    if (afterManMatch) {
      amount += parseInt(afterManMatch[1].replace(/,/g, ''), 10)
    }
  } else if (okuMatch) {
    // 億の後の端数（万がない場合）: "億123円"
    const afterOkuMatch = formatted.match(/億([0-9,]+)円/)
    if (afterOkuMatch) {
      amount += parseInt(afterOkuMatch[1].replace(/,/g, ''), 10)
    }
  } else {
    // 億も万もない場合: "3,456円"
    const enMatch = formatted.match(/([0-9,]+)円/)
    if (enMatch) {
      amount += parseInt(enMatch[1].replace(/,/g, ''), 10)
    }
  }

  return amount
}
